package com.neurobs.js.dom

import com.neurobs.js.view.Container
import org.scalajs.dom.html.Div
import org.scalajs.dom.raw.HTMLElement
import scalatags.JsDom.all._

import scala.collection.mutable.ArrayBuffer

case class GridCell(e: HTMLElement, align: Option[String] = None)

class Grid (fullWidth: Boolean, val emGapSize: Double, defaultAlign: String = Grid.alignLeft, alignItems: String = Grid.alignItemsNormal) {

	/** The outermost element that contains the grid */
	val container: Div = {
		val g = div().render
		g.style.margin = "0"
		g.style.display = if (fullWidth) "grid" else "inline-grid"
		g.style.setProperty(propertyName = "grid-gap", value = s"${emGapSize}em")
		g.style.setProperty(propertyName = "align-items", value = alignItems)
		g
	}

	/** All the children div's in the grid div, by row */
	private val rows: ArrayBuffer[Array[HTMLElement]] = ArrayBuffer()

	/** Generates a value for the 'grid-area' CCS property for a 1x1 grid element
	  *
	  * @param rowIndex Base-0 index of the row of the cell
	  * @param colIndex Base-0 index of the column of the cell
	  * @return Value for the 'grid-area' CSS property
	  */
	private def gridAreaValue(rowIndex: Int, colIndex: Int): String = {
		s"${rowIndex + 1} / ${colIndex + 1} / span 1 / span 1"
	}

	/** Sets the 'grid-area' CSS property for all cells in a row
	  *
	  * @param row The cell elements
	  * @param rowIndex Base-0 index for the row
	  */
	private def setRowAreas(row: Array[HTMLElement], rowIndex: Int): Unit = {
		for ((e, i) <- row.view.zipWithIndex) {
			e.style.setProperty("grid-area", gridAreaValue(rowIndex, i))
		}
	}

	class RowId (private[Grid] val elements: Array[HTMLElement])

	def count: Int = rows.length

	/** Adds a row at the bottom
	  *
	  * @param cells Content of row
	  * @return Id used to identify the row
	  */
	def addRowCells(cells: Seq[GridCell], textAlign: Option[String] = None): RowId = insertRowCells(cells, rows.length, textAlign)

	def addRow(cell: GridCell, textAlign: Option[String] = None): RowId = addRowCells(List(cell), textAlign)

	private def resetEndRows(startIndex: Int): Unit = {
		for ((row, i) <- rows.view.zipWithIndex.drop(startIndex)) {
			setRowAreas(row, i)
		}
	}

	/** Inserts a row at a given index
	  *
	  * @param cells Content of row
	  * @param index Index of the new row. If out of range, the new row is either
	  *              at the beginning or at the end
	  * @return Id used to identify the row
	  */
	def insertRowCells(cells: Seq[GridCell], index: Int, textAlign: Option[String] = None): RowId = {
		val i = if (index < 0) 0 else if (index > rows.length) rows.length else index
		val row: Array[HTMLElement] = cells.toArray.map { e =>
			val styleValue = s"text-align: ${e.align.getOrElse(textAlign.getOrElse(defaultAlign))}"
			div(style := styleValue, e.e).render
		}
		rows.insert(i, row)
		resetEndRows(i)
		row.foreach(e => container.appendChild(e))
		new RowId(row)
	}

	def insertRow(cell: GridCell, index: Int, textAlign: Option[String] = None): RowId = insertRowCells(List(cell), index, textAlign)

	/** Inserts a row after a given row
	  *
	  * @param cells Content of row
	  * @param id Id of existing row. If the row is not found, the row is added to the end
	  * @return Id used to identify the row
	  */
	def insertCellsAfter(cells: Seq[GridCell], id: RowId, textAlign: Option[String] = None): RowId = {
		val i = rows.indexOf(id.elements)
		insertRowCells(cells, if (i < 0) rows.length else i + 1, textAlign)
	}

	def insertAfter(cell: GridCell, id: RowId, textAlign: Option[String] = None): RowId = insertCellsAfter(List(cell), id, textAlign)

	/** Removes row by id
	  *
	  * @param id The Id returned by the addRow methods
	  */
	def removeRow(id: RowId): Unit = {
		removeRow(rows.indexOf(id.elements))
	}

	/** Removes the row by index
	  *
	  * @param rowIndex Index of row. If out of range, nothing happens
	  */
	def removeRow(rowIndex: Int): Unit = {
		if ( (rowIndex >= 0) && (rowIndex < rows.length) ) {
			rows(rowIndex).foreach(container.removeChild(_))
			rows.remove(rowIndex)
			resetEndRows(rowIndex)
		}
	}

	/** Removes all rows */
	def clear(): Unit = while (count > 0) removeRow(rowIndex = 0)

	def resize(size: Int): Unit = if (size >= 0) {
		while (count > size) removeRow(rowIndex = count - 1)
	}
}

class HideableRowGrid (fullWidth: Boolean, emGapSize: Double, defaultAlign: String = Grid.alignLeft, alignItems: String = Grid.alignItemsNormal) {
	private val grid: Grid = new Grid(fullWidth, emGapSize, defaultAlign, alignItems)
	private val rows: ArrayBuffer[(Seq[GridCell], Option[grid.RowId])] = ArrayBuffer()

	def addRow(row: GridCell): Unit = {
		rows.append((Array(row), Some(grid.addRow(row))))
	}
	def addRow(row: Seq[GridCell]): Unit = {
		rows.append((row, Some(grid.addRowCells(row))))
	}

	def insertRow(cell: GridCell, index: Int): Unit = {
		rows.insert(index, (Array(cell), Some(grid.insertRow(cell, index))))
	}
	def insertRow(cell: Seq[GridCell], index: Int, align: Option[String] = None): Unit = {
		rows.insert(index, (cell, Some(grid.insertRowCells(cell, index))))
	}

	/** Shows or hides a row
	 *
	 * @param row Left-most element of the row when it was added
	 * @param visible Whether to show the row
	 */
	def setRowVisible(row: HTMLElement, visible: Boolean): Unit = {
		val i = rows.indexWhere(_._1(0).e == row)
		if ( (i >= 0) && (rows(i)._2.isDefined != visible) ) {
			if (visible) {
				val newIndex = rows.view.take(i).count(_._2.isDefined)
				val old = rows(i)._1
				rows(i) = (old, Some(grid.insertRowCells(old, newIndex)))
			}
			else {
				grid.removeRow(rows(i)._2.get)
				rows(i) = (rows(i)._1, None)
			}
		}
	}

	def removeRow(row: HTMLElement): Unit = {
		val i = rows.indexWhere(_._1(0).e == row)
		if (i >= 0) {
			rows(i)._2.foreach(grid.removeRow(_))
			rows.remove(i, 1)
		}
	}

	val container = grid.container
}

object Grid {
	val alignLeft = "left"
	val alignCenter = "center"
	val alignRight = "right"

	val alignItemsNormal = "normal"
	val alignItemsCenter = "center"

	implicit def fromHTML(e: HTMLElement): GridCell = GridCell(e)
	implicit def fromHTMLPair(p: (HTMLElement, String)): GridCell = GridCell(p._1, Some(p._2))
	implicit def fromContainer(e: Container): GridCell = GridCell(ElementFactory.toElement(e))
	implicit def fromContainerPair(p: (Container, String)): GridCell = GridCell(ElementFactory.toElement(p._1), Some(p._2))
}